import type { CheckItem } from '@/content/checks'
import type { Photo } from '@/content/sections'
import { LINKS } from '@/content/pages/links'

/**
 * The Future 2030 Manufacturing outlook page — its copy and its figures, apart
 * from the component that draws them.
 *
 * **The second Future 2030 outlook with a designed page of its own**, and the
 * eleventh report overall. Asked for on 2026-08-28, hours after the Automotive
 * one and in the same words — *"referans alarak ilgili sayfayı birebir tıpa tıp
 * aynısını tasarla"* — as a five-part full-page capture of digitopia.co's
 * Manufacturing outlook page plus the six images that page carries: the robot-arm
 * cover art, the DMI forecast chart, the three requirement photographs and the
 * report mockup.
 *
 * So the bands are the reference's, in the reference's order: hero → the outlook
 * beside Figure 1 → a dark band of three moves → the five predictions with the
 * mockup → the pointer at the maturity report → the close. The two shared closing
 * bands (`content/reportClosing.ts`) still sit where they sit on every other
 * report page.
 *
 * **Where it differs from the Automotive outlook**, which is the sibling page and
 * shares its component:
 *
 *  * the hero art is not a guess here. digitopia.co's Manufacturing hero really
 *    does carry the robot arm on a circuit board to the right of the words, so
 *    there is no open question about whether that emptiness was the point.
 *  * the outlook band opens with one line above the three trajectories and closes
 *    with one under them ("Your actions today shape your outcomes tomorrow"), and
 *    the trajectories are Leaders → Average → Laggards rather than the other way
 *    round. Both are the reference's.
 *  * the predictions are five plain statements rather than a label and a figure,
 *    so nothing in that band carries a number a reader could check.
 *  * the maturity band points at the **2026** report, which was decided rather
 *    than transcribed — see `OUTLOOK_MF_MATURITY`.
 *
 * **Where the words come from.** Everything here is transcribed from the five
 * captures, which are full-page renders read down to fit — the *shape* of the page
 * is certain and the *wording* is a careful reading of body copy set a handful of
 * pixels tall. Every band that carries prose therefore carries a `check`, the same
 * way `content/outlookAutomotive.ts` does, and `outlookManufacturingChecks()`
 * feeds them into the same manifest so a production build cannot ship them unread.
 *
 * **What the live page has that this page cannot.** Its Download buttons and its
 * closing band open an embedded HubSpot form. These documents are prerendered with
 * no client bundle at all (`src/prerender.tsx`), so a form script would never run —
 * and the other thirty reports on this site hand the reader the PDF from our own
 * bucket instead. Every Download button here is that PDF, and
 * `OUTLOOK_MF_CLOSE.check` is what keeps the gating question open.
 */

/** The report this page belongs to — the branch `pages/Reports.tsx` takes on it. */
export const OUTLOOK_MF_SLUG = 'manufacturing-industry-outlook-2030'

/**
 * The Manufacturing Industry Focus cut of the Digital & AI Maturity Report 2026 —
 * where this page's maturity band sends the reader.
 *
 * Written out rather than imported: that report is one of the twenty drawn by the
 * shared template in `pages/Reports.tsx`, so it has no content module of its own
 * to export a slug from. It is the literal passed to `focus()` in
 * `content/reports.ts`, and the two have to stay in step.
 */
export const DAIMI_MF_SLUG = 'digital-and-ai-maturity-report-2026-manufacturing-industry'

/**
 * The hero.
 *
 * `lede` is set bold above the paragraph because that is how the live hero sets
 * it — one question in bold, the reason for it underneath — and the button says
 * "Download the Report" rather than the "Download Report" every other band uses,
 * which is also the live page's wording.
 */
export const OUTLOOK_MF_INTRO = {
  lede: 'Will your company still lead in 2030?',
  body: 'The next five years will define the winners of industrial transformation. See how your digital maturity compares, then get ready to accelerate.',
  downloadLabel: 'Download the Report',
  /**
   * The cover art supplied with this request — the robot arm on a circuit board,
   * which is the artwork behind the report cover with none of its lettering on it.
   * Unlike the Automotive page, this is where the live hero puts it too, at the
   * same size and on the same side.
   */
  photo: {
    src: '/photo/future-2030-manufacturing-digital-transformation.webp',
    alt: 'A robotic arm rendered in chrome, standing on a glowing circuit board',
  } satisfies Photo,
  check:
    'The hero’s two lines are transcribed from the reference capture rather than supplied as text. Read them back against digitopia.co before production. The image, its side and its size are the live page’s.',
}

/**
 * The outlook band: Figure 1 on the left, the three trajectories on the right.
 *
 * The three are one block on the live page in which each line opens with a bolded
 * label — "Leaders", "Average companies", "Laggards". Split into `label` and
 * `body` here so the label can be set rather than emphasised inside a sentence,
 * which is the call every designed report page on this site has made.
 *
 * `intro` and `outro` are the two single lines the live band wraps them in, and
 * the Automotive band has neither.
 */
export const OUTLOOK_MF_FIGURE = {
  heading: 'Manufacturing Industry Outlook',
  intro: 'Digitopia’s forecast shows three possible futures:',
  points: [
    {
      label: 'Leaders',
      body: 'may reach 4.49 maturity by 2029, cementing their market dominance.',
    },
    {
      label: 'Average companies',
      body: 'face a critical decision point: adapt or fade into mediocrity.',
    },
    {
      label: 'Laggards',
      body: 'must act now or risk obsolescence.',
    },
  ],
  outro: 'Your actions today shape your outcomes tomorrow.',
  /**
   * Figure 1, the file supplied with this request: five years of actuals (2020–24)
   * and five of forecast (2025–29), the leader, average and laggard trajectories
   * each fanning out into an optimistic, moderate and pessimistic band. It is
   * 1200×784 on a white ground, so it gets a white card of its own and scales to it
   * whole — ten year columns with three labelled values each do get small on a
   * phone, but a figure the reader has to drag sideways reads as broken.
   */
  photo: {
    src: '/photo/future-2030-manufacturing-dmi-forecast.webp',
    alt: 'Manufacturing DMI evolution 2020 to 2029: actual leader, average and laggard scores to 2024, then optimistic, moderate and pessimistic forecast bands to 2029',
  } satisfies Photo,
  caption: [
    'Figure 1: Manufacturing Industry DMI Evolution (2020–2029) with Scenario-Based Forecasts',
    'Leader, Average, and Laggard Trajectories under Optimistic, Moderate, and Pessimistic Projections',
  ],
  check:
    'Figure 1 is the file you supplied and is shown whole. Two readings wanted on the words beside it. The leaders’ line turns on one figure — “4.49 maturity by 2029” — which is the number the chart’s top-right label carries, so the two agree, but the report has to stand behind it. And the live page calls the index DMI here while every other page on this site calls it DAIMI, the Digital & AI Maturity Index; the chart itself is lettered DMI, so both were left as they are. Say which name this page should use.',
}

/**
 * The dark requirements band: two headline lines, then three cards.
 *
 * The live band's second line is not a sentence but a title — "Three Moves to
 * Win 2030" — so it is set lighter and larger under the headline rather than as a
 * second claim, which is what the shared component already does with it.
 */
export const OUTLOOK_MF_REQUIREMENTS = {
  heading: 'The gap will widen unless you close it now.',
  subheading: 'Three Moves to Win 2030',
  /** The eyebrow above each card's title, numbered in the order the live band has. */
  eyebrow: 'How to Get There',
  items: [
    {
      title: 'Operational Excellence',
      body: 'Leverage IoT, robotics, and digital twins to improve productivity.',
      linkLabel: 'Digital Solutions',
      linkHref: LINKS.solutions,
      photo: {
        src: '/photo/manufacturing-operational-excellence.webp',
        alt: 'An engineer on a factory floor reading a plant layout on a laptop',
      } satisfies Photo,
    },
    {
      title: 'AI-First Mindset',
      body: 'Integrate AI across forecasting, automation, and decision-making.',
      linkLabel: 'Artificial Intelligence',
      linkHref: LINKS.enterpriseAi,
      photo: {
        src: '/photo/manufacturing-ai-first-mindset.webp',
        alt: 'A process engineer in a white coat working at a digital twin projected over the plant',
      } satisfies Photo,
    },
    {
      title: 'People & Culture',
      body: 'Upskill your workforce and foster a digital-first mindset.',
      linkLabel: 'Digital Upskilling',
      linkHref: LINKS.transformation,
      photo: {
        src: '/photo/manufacturing-people-and-culture.webp',
        alt: 'A technician in a hard hat setting up a robot cell on a production line',
      } satisfies Photo,
    },
  ],
  check:
    'Two things. The two headline lines and the three card texts are transcribed from the reference capture. And the third card’s link is the one destination that had to change: the live page sends “Digital Upskilling” to a solution page digitopia.co has and this site does not — it came across in the import as a rebuild source rather than as a page, so linking to it would be a dead link. It points at Transformation Management until that page is built. The other two are the live destinations. This is the same substitution the Automotive outlook page makes, and one answer settles both.',
}

/**
 * The predictions band, with the report mockup beside it.
 *
 * Five plain statements rather than the Automotive page's label-and-figure pairs,
 * because that is what the live band prints: a lead-in line and five bullets.
 */
export const OUTLOOK_MF_PREDICTIONS = {
  heading: 'Five Predictions That Will Reshape Manufacturing',
  intro: 'The report outlines 5 bold predictions:',
  items: [
    'Smart factories will become the new standard',
    'Agentic AI will fuel hyper-automation',
    'Supply chain resilience will define market trust',
    'Robotics will go mainstream',
    'Sustainability will drive competitive edge',
  ],
  /** The mockup supplied with this request — the Future 2030 cover, not a maturity cut's. */
  photo: {
    src: '/photo/future-2030-outlook-manufacturing-mockup.webp',
    alt: 'Two copies of Future 2030: Digital Transformation Outlook for Manufacturing',
  } satisfies Photo,
  check:
    'All five predictions are transcribed from the reference capture. None of them carries a figure, so what is wanted here is a reading of the wording rather than a check of a number.',
}

/**
 * The band pointing at the maturity report.
 *
 * **The one band whose content is deliberately not the live page's, and the only
 * change in this request that was asked for in words rather than shown in a
 * picture.** digitopia.co sends the reader to a *2024* Manufacturing Digital
 * Maturity Report; the instruction with the captures was *"en aşağıda önceden
 * konuştuğumuz gibi 2024'e değil 2026 DAIMI report'a yönlendirelim"*. So the CTA,
 * the cover and the sentence all name the Manufacturing Industry Focus cut of the
 * Digital & AI Maturity Report 2026, which is the report this site actually
 * publishes — and "five years of in-depth assessments" is "six years", the figure
 * this site's own closing band states.
 *
 * That is settled, so it is not what `check` asks about: the same swap was left
 * open on the Automotive page and has now been answered for both.
 */
export const OUTLOOK_MF_MATURITY = {
  heading: 'Before You Plan for 2030, Understand Where You Are Today',
  body: 'The road to digital leadership starts with clarity. Our Manufacturing Industry Focus cut of the Digital & AI Maturity Report 2026 benchmarks your industry’s current state, based on real data from six years of in-depth assessments.',
  points: [
    'Discover where your organization stands in relation to leaders, laggards, and the industry average.',
    'Use these insights to validate your priorities and define the right next steps.',
  ],
  ctaLabel: 'Explore the Manufacturing Industry Focus report',
  ctaHref: `/en/impact-labs/reports/${DAIMI_MF_SLUG}`,
  /** That report's cover, which this site already holds. */
  photo: {
    src: '/photo/daimi-report-2026-manufacturing-cover.webp',
    alt: 'Digital & AI Maturity Report 2026, Manufacturing Focus',
  } satisfies Photo,
  check:
    'The destination here is decided, not transcribed: this band points at the 2026 report rather than the 2024 Manufacturing Digital Maturity Report the live page offers, which is what you asked for. What still wants a reading is the sentence around it — the live one says “five years of in-depth assessments” where this site says six, which is the figure the closing band on every report page here states.',
}

/** The closing band, which is where the live page's gated form sits. */
export const OUTLOOK_MF_CLOSE = {
  heading: 'The Next Era of Industrial Leadership',
  body: 'Don’t get left behind. Learn how leading manufacturers are preparing for the next decade, and what your company can do today. Get insights from five years of benchmark data, plus five predictions shaping the future of manufacturing.',
  expertLabel: 'Talk to an expert',
  // `#contact` rather than the Contact Us page: the form sits at the foot of
  // this very page, so the button scrolls instead of navigating away.
  expertHref: '#contact',
  /**
   * Resolved 2026-08-30. This band asked whether the report should sit behind
   * the form the live page gates it with, because every Download button here
   * linked straight to the bucket. It now does: the buttons carry
   * `data-gated-download` (`lib/gatedDownload.ts`), so the reader is asked for
   * an email at the foot of this page and `website-form-lead` sends the PDF to
   * it. There is no direct link left on the page to gate.
   */
  check: undefined,
}

/**
 * The page's open questions, in the shape `build-manifest.json` records copy
 * checks in — the same mechanism `outlookAutomotiveChecks()` uses, and appended to
 * the same list in `reportChecks()`.
 *
 * `'content'` rather than `'asset'`: nothing here is a missing file. This page
 * arrived with all six of its images. What is left is transcription nobody has
 * read against the original, one destination this site cannot offer, and the
 * gating decision.
 */
export function outlookManufacturingChecks(): CheckItem[] {
  const sections: { section: string; check?: string }[] = [
    { section: 'hero', check: OUTLOOK_MF_INTRO.check },
    { section: 'outlook', check: OUTLOOK_MF_FIGURE.check },
    { section: 'requirements', check: OUTLOOK_MF_REQUIREMENTS.check },
    { section: 'predictions', check: OUTLOOK_MF_PREDICTIONS.check },
    { section: 'maturity', check: OUTLOOK_MF_MATURITY.check },
    { section: 'download', check: OUTLOOK_MF_CLOSE.check },
  ]
  return sections
    .filter((s): s is { section: string; check: string } => Boolean(s.check))
    .map((s) => ({
      locale: 'en',
      page: `impact-labs/reports/${OUTLOOK_MF_SLUG}`,
      section: s.section,
      text: s.check,
      severity: 'content',
    }))
}
